"use client";

import { GripVertical, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import type { DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core";
import { ColumnDef } from "./types";

interface DataTableHeaderProps<T> {
  column: ColumnDef<T>;
  currentSortKey?: string | null;
  sortOrder?: "asc" | "desc" | null;
  onSortChange?: (sortKey: string) => void;
  /** @dnd-kit sortable: ref для клітинки */
  sortableCellRef?: (node: HTMLTableHeaderCellElement | null) => void;
  sortableCellStyle?: React.CSSProperties;
  sortableListeners?: DraggableSyntheticListeners;
  sortableAttributes?: DraggableAttributes;
}

export function DataTableHeader<T>({
  column,
  currentSortKey,
  sortOrder,
  onSortChange,
  sortableCellRef,
  sortableCellStyle,
  sortableListeners,
  sortableAttributes,
}: DataTableHeaderProps<T>) {
  const isSortable = column.sortable && column.sortKey && onSortChange;
  const isActive = isSortable && currentSortKey === column.sortKey;
  const isDraggable = !!(sortableCellRef && column.id);

  const gripClass = "w-3 h-3 text-[#8f98a0] shrink-0 pointer-events-none";
  const iconClass = "w-3 h-3";

  const content = (
    <div
      className={`flex items-center gap-1.5 ${
        column.align === "right"
          ? "justify-end"
          : column.align === "center"
            ? "justify-center"
            : ""
      }`}
    >
      {isDraggable && (
        <GripVertical className={gripClass} aria-hidden />
      )}
      {column.header}
      {isSortable && (
        isActive && sortOrder ? (
          sortOrder === "asc" ? (
            <ArrowUp className={`${iconClass} text-[#66c0f4]`} />
          ) : (
            <ArrowDown className={`${iconClass} text-[#66c0f4]`} />
          )
        ) : (
          <ArrowUpDown className={`${iconClass} text-[#8f98a0] opacity-60`} />
        )
      )}
    </div>
  );

  const baseClass = "py-1.5 px-2 font-semibold text-[#c7d5e0] text-[11px] uppercase tracking-wide h-8";

  const thClass = `${baseClass} ${
    column.align === "center"
      ? "text-center"
      : column.align === "right"
        ? "text-right"
        : "text-left"
  } ${column.width || ""} ${isDraggable ? "cursor-grab active:cursor-grabbing" : ""}`;

  const handleClick = () => {
    if (isSortable) onSortChange(column.sortKey!);
  };

  return (
    <th
      ref={sortableCellRef}
      style={sortableCellStyle}
      className={isSortable ? `${thClass} hover:bg-[#1f2e3f] transition-colors` : thClass}
      onClick={isSortable ? handleClick : undefined}
      {...(sortableAttributes ?? {})}
      {...(sortableListeners ?? {})}
    >
      {content}
    </th>
  );
}
